import { defineStore } from 'pinia'
import { ref } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useLoadingStore } from '@/modules/core/stores/loadingStore'
import { useErrorManagerComposable } from '@/modules/core/composables/errorManagerComposable'
import { useNavigation } from '@/modules/core/composables/navigationComposable'
import { useToastStore } from '@/modules/core/stores/toastStore'
import { useCommonStore } from '@/modules/core/stores/commonStore'
import { ButtonTypeEnum, ErrorDisplayTypeEnum, UserRoleEnum } from '@/modules/core/models/enums'

import ENDPOINTS from '@/modules/core/constants/apiEndpoints'
import ServiceSchemaError from '@/modules/core/models/errors/ServiceSchemaError'

import MiddlewareService from '@/modules/core/services/MiddlewareService'
import DXPService from '@/modules/core/services/DXPService'
import ServiceError from '@/modules/core/models/errors/ServiceError'
import ModalButton from '@/modules/core/models/modalMessage/ModalButton'
import Insurance from '@/modules/common/common/models/APP/Insurance'
import RaterPriceResponse from '@/modules/common/common/models/MID/responses/RaterPriceResponse'
import GetProductResponse from '@/modules/common/common/models/DXP/responses/GetProductResponse'

import LifeConfigurations from '@/modules/life/common/models/APP/LifeConfigurations'
import LifeInsurance from '@/modules/life/common/models/APP/LifeInsurance'
import LifeRaterAnswers from '@/modules/life/rater/models/APP/LifeRaterAnswers'
import LifeCommonMapper from '@/modules/life/common/mappers'
import LifeMapper from '@/modules/life/rater/mappers/index'
import CommonMapper from '@/modules/common/common/mappers/index'
import ProductId from '@/modules/common/common/models/DXP/ProductId'
import type DXPProduct from '@/modules/common/common/models/DXP/Product'
import type RaterPricing from '@/modules/common/common/models/MID/RaterPricing'
import type BaseRaterStore from '@/modules/common/rater/models/APP/BaseRaterStore'
import ConsoleError from '@/modules/core/models/errors/ConsoleError'
import PAGES from '@/modules/life/rater/constants/pages'
import type FormPage from '@/modules/common/common/models/APP/FormPage'
import CoverageBasic from '@/modules/common/common/models/APP/CoverageBasic'
import GTMEventType from '@/modules/core/models/GTMEventType'
import { useGTMStore } from '@/modules/core/stores/gtmStore'
import { useSharedGtmDataStore } from '@/modules/core/stores/sharedGtmDataStore'
import { useSupportModalStore } from '@/modules/core/stores/supportModalStore'

export const useLifeStore = defineStore('raterLife', () => {
  const errorManager = useErrorManagerComposable()
  const toastStore = useToastStore()
  const loadingStore = useLoadingStore()
  const commonStore = useCommonStore()
  const router = useRouter()
  const { pushPreservingMCode } = useNavigation()
  const { t } = useI18n()
  const supportModalStore = useSupportModalStore()
  const gmtStore = useGTMStore()
  const sharedGtmDataStore = useSharedGtmDataStore()

  const globalConfig = ref(new LifeConfigurations())
  const raterAnswers = ref(new LifeRaterAnswers())
  const insuranceProposals = ref<LifeInsurance[]>([])
  const coverages = ref<CoverageBasic[]>([])

  function getPages(): Record<string, FormPage> {
    return PAGES
  }

  function getBranchConfiguration(): LifeConfigurations {
    return globalConfig.value
  }

  function getInsuranceProposals() {
    return insuranceProposals.value
  }

  function clearRaterAnswers(): void {
    raterAnswers.value = new LifeRaterAnswers()
  }

  function getRateFormAnswers() {
    return raterAnswers.value as LifeRaterAnswers
  }

  function setRateFormAnswers(answers: Partial<LifeRaterAnswers>) {
    if (!answers) return

    raterAnswers.value.updateAttributes(answers)
  }

  function getMostRelevantProducts(insurances: LifeInsurance[]) {
    const sortedItems = insurances.sort((a, b) => a.order - b.order)

    return sortedItems.slice(0, 2)
  }

  async function loadReevaluationOptions() {
    try {
      if (
        globalConfig.value.reevaluationOptions &&
        globalConfig.value.reevaluationOptions?.length > 0
      )
        return
      const endpoint = ENDPOINTS.GET_REEVALUATION_OPTIONS
      const { error, data } = await MiddlewareService.post(endpoint, {
        BusinessLine: globalConfig.value.businessLineId,
        ClientCode: '24568',
        ProductCode: '408017'
      })
      if (error) {
        throw new ServiceError(data)
          .setDisplayType(ErrorDisplayTypeEnum.MODAL)
          .setModalDescription('copy_error_service_description')
          .setButtons([
            new ModalButton({
              text: 'header.help-text',
              icon: 'icon-message-text-01',
              type: ButtonTypeEnum.OUTLINE,
              onclick: () => {
                gmtStore.sendEvent({
                  event: GTMEventType.CALLMEBACK_BTN,
                  click_name: 'support-modal-btn',
                  click_id: 'support-modal-btn',
                  btn_label: t('header.help-text'),
                  value: sharedGtmDataStore.policyValue
                })
                supportModalStore.setNavTo('exit')
                supportModalStore.setOpenModal(true)
              }
            }),
            new ModalButton({
              text: 'copy_button_return',
              navTo: 'exit',
              type: ButtonTypeEnum.OUTLINE
            })
          ])
      }
      globalConfig.value.setReevaluationOptions(data)
    } catch (error) {
      loadingStore.stopLoading()
      errorManager.handleError(error)
    }
  }

  async function loadConfigurationInner() {
    try {
      const endpoint = ENDPOINTS.DXP_GET_BRANCH_LIFE
      const { error, data } = await DXPService.get(endpoint)
      if (error) {
        throw new ServiceError(data)
          .setDisplayType(ErrorDisplayTypeEnum.MODAL)
          .setModalDescription('copy_error_service_description')
          .setButtons([
            new ModalButton({
              text: 'header.help-text',
              icon: 'icon-message-text-01',
              type: ButtonTypeEnum.OUTLINE,
              onclick: () => {
                gmtStore.sendEvent({
                  event: GTMEventType.CALLMEBACK_BTN,
                  click_name: 'support-modal-btn',
                  click_id: 'support-modal-btn',
                  btn_label: t('header.help-text'),
                  value: sharedGtmDataStore.policyValue
                })
                supportModalStore.setNavTo('exit')
                supportModalStore.setOpenModal(true)
              }
            }),
            new ModalButton({
              text: 'copy_button_return',
              navTo: 'exit',
              type: ButtonTypeEnum.OUTLINE
            })
          ])
      }

      globalConfig.value = new LifeConfigurations(data)
      raterAnswers.value.businessLine = globalConfig.value.businessLineId
    } catch (error) {
      loadingStore.stopLoading()
      errorManager.handleError(error)
    }
  }

  async function loadConfiguration() {
    await loadConfigurationInner()
    await loadReevaluationOptions()
  }

  async function loadCoverages({
    insuranceType,
    clientCode,
    productCode,
    preventCache = false
  }: {
    insuranceType?: string | undefined
    clientCode?: string | undefined
    productCode?: string | undefined
    preventCache?: boolean
  }): Promise<CoverageBasic[]> {
    if (coverages.value.length && !preventCache) {
      return coverages.value
    }

    const { businessLineId, defaultInsuranceType } = globalConfig.value
    coverages.value = await commonStore.loadCoverages({
      businessLine: businessLineId,
      insuranceType: insuranceType ?? defaultInsuranceType!,
      clientCode,
      productCode
    })

    return coverages.value
  }

  async function sendRate(
    options: {
      preventRouting?: boolean
      answers?: LifeRaterAnswers | undefined
      externalErrorHandling?: boolean
      checkLocation?: string
    } = {}
  ) {
    options.preventRouting = Boolean(options.preventRouting)
    options.externalErrorHandling = Boolean(options.externalErrorHandling)
    if (!options.checkLocation) {
      options.checkLocation = location.pathname
    }

    try {
      const loadingTexts = ['common.loading.processing']
      if (!options.preventRouting) {
        loadingTexts.push('common.loading.looking-for-best-products')
      }
      loadingStore.startLoading(...loadingTexts)

      const endpoint = ENDPOINTS.RATER_CALCULATE_PRICE_LIFE
      const answers = options.answers ?? (raterAnswers.value as LifeRaterAnswers)
      const request = LifeMapper.GetPricingMapper.toRequest(
        answers,
        true,
        commonStore.infoMediator?.id
      )
      const { error, data } = await MiddlewareService.post(endpoint, request)
      if (error) {
        throw new ServiceError(data).setDisplayType(ErrorDisplayTypeEnum.MODAL)
      }

      if (options.answers) {
        raterAnswers.value.updateAttributes(options.answers)
      }

      const response = new RaterPriceResponse(data)
      if (response.listPricing.length === 0) {
        throw new ServiceSchemaError()
          .setTitle('copy_rater-empty-error-modal_title')
          .setModalDescription('copy_rater-empty-error-modal_description')
          .setButtons([
            new ModalButton({
              text: 'components.InsuredSubheaderComponent.button'
            }),
            new ModalButton({
              text: 'copy_button_init_return',
              navTo: 'rater-nav',
              type: ButtonTypeEnum.OUTLINE
            })
          ])
      }

      setRateFormAnswers({
        quotationId: response.quotationId,
        businessLine: response.listPricing[0].businessLine
      })
      const products = data.listPricing.map((l: RaterPricing) => {
        return new ProductId({
          productCode: l.productCode,
          clientCode: l.clientCode
        })
      })
      const dxpData = await _fetchDXPInsurances(products)
      insuranceProposals.value = _loadCompleteSheetProducts(dxpData, data.listPricing)

      if (insuranceProposals.value.length === 0) {
        const modalButtons = [
          new ModalButton({
            text: 'components.InsuredSubheaderComponent.button'
          }),
          new ModalButton({
            text: 'copy_button_init_return',
            navTo: 'rater-nav',
            type: ButtonTypeEnum.OUTLINE
          })
        ]
        throw new ServiceSchemaError()
          .setTitle('copy_rater-empty-error-modal_title')
          .setModalDescription('copy_rater-empty-error-modal_description')
          .setButtons(modalButtons)
      }

      if (!options.preventRouting && options.checkLocation === location.pathname) {
        // check pathname to prevent to push a route when user clicks to go to back or other stuff
        await pushPreservingMCode(router, { name: `life-insurance-proposals` })
      }
    } catch (error) {
      if (options.externalErrorHandling) {
        throw error
      }

      errorManager.handleError(error)
    } finally {
      loadingStore.stopLoading()
    }
  }

  //** PRIVATE SUPPORT METHODS */
  // todo - COMMON composable or life/common composable?
  async function _fetchDXPInsurances(products: ProductId[]) {
    const endpoint = ENDPOINTS.DXP_GET_PRODUCT
    const request = LifeCommonMapper.GetProductsMapper.toRequest(products)
    const { error, data } = await DXPService.post(endpoint, request)
    if (error) throw new ServiceError(data)

    return data.map((d: any) => new GetProductResponse(d).product)
  }

  // todo - COMMON composable or life/common composable?
  function _loadCompleteSheetProducts(
    DXPProducts: DXPProduct[],
    MIDProducts: RaterPricing[]
  ): LifeInsurance[] {
    const products: LifeInsurance[] = []
    DXPProducts.forEach((DXPProduct) => {
      const MIDProduct = MIDProducts.find((p) => {
        return String(DXPProduct.productId) === String(p.productCode)
      })

      if (MIDProduct) {
        const insurance = LifeCommonMapper.GetProductsMapper.fromResponse(DXPProduct, MIDProduct)
        products.push(insurance)
      }
    })

    return products
  }

  async function hireProduct(
    quotationId: string | null,
    startDate: Date,
    billingCycle: string,
    product: LifeInsurance,
    role: UserRoleEnum
  ): Promise<{ valid: boolean }> {
    let valid = true

    try {
      loadingStore.startLoading('Procesando...')

      const { error, data } = await _saveQuotation(
        quotationId,
        startDate,
        billingCycle,
        product,
        role
      )
      if (error) throw new ServiceError(data)
    } catch (error) {
      console.error(error)
      valid = false
    } finally {
      loadingStore.stopLoading()
    }

    return { valid }
  }

  async function loadSpecificProduct(productCode: string, clientCode: string) {
    try {
      const dxpData = await _fetchDXPInsurances([{ productCode, clientCode }])
      if (!dxpData.length) {
        throw new ConsoleError({
          message: `DXP Error: No se ha encontrado la ficha para el producto ${productCode}`
        })
      }

      const DXPProduct = dxpData[0]
      const insurance = LifeCommonMapper.GetProductsMapper.fromResponse(DXPProduct, {})

      raterAnswers.value = new LifeRaterAnswers({
        product: insurance,
        businessLine: globalConfig.value.businessLineId
      })
    } catch (error) {
      errorManager.handleError(error)
      throw error
    }
  }

  async function _saveQuotation(
    quotationId: string | null,
    startDate: Date,
    billingCycle: string,
    product: Insurance,
    holderRole: UserRoleEnum
  ) {
    if (!quotationId) throw new Error('Quotation id is required')

    const endpoint = ENDPOINTS.SAVE_QUOTATION_LIFE
    const request = CommonMapper.SaveQuotationMapper.toRequest(
      quotationId,
      startDate,
      billingCycle,
      product,
      holderRole
    )
    const { error, data } = await MiddlewareService.post(endpoint, request)
    return { error, data }
  }

  async function sendBudget(
    quotationNumber: string,
    email: string,
    privacyPolicyConsent: boolean,
    quotationId: string | null,
    startDate: Date,
    billingCycle: string,
    product: LifeInsurance,
    holderRole: UserRoleEnum
  ) {
    try {
      loadingStore.startLoading('Procesando...')
      const quotationResult = await _saveQuotation(
        quotationId,
        startDate,
        billingCycle,
        product,
        holderRole
      )
      if (quotationResult.error) {
        throw new ServiceError(quotationResult.data)
          .setDisplayType(ErrorDisplayTypeEnum.TOAST_IN_MODAL)
          .setTitle(t('copy_budget-service_error'))
      }
      const endpoint = ENDPOINTS.SEND_BUDGET
      const request = { quotationNumber, to: email, privacyPolicyConsent }
      const { error, data } = await MiddlewareService.post(endpoint, request)
      if (error) {
        throw new ServiceError(data)
          .setDisplayType(ErrorDisplayTypeEnum.TOAST_IN_MODAL)
          .setTitle(t('copy_budget-service_error'))
      }

      toastStore.successToast('copy_budget-service_success')
    } catch (error) {
      errorManager.handleError(error)
      throw error
    } finally {
      loadingStore.stopLoading()
    }
  }

  const store: BaseRaterStore<LifeInsurance, LifeRaterAnswers, LifeConfigurations> & {
    loadCoverages: ({
      insuranceType,
      clientCode,
      productCode,
      preventCache
    }: {
      insuranceType?: string | undefined
      clientCode?: string | undefined
      productCode?: string | undefined
      preventCache?: boolean
    }) => Promise<CoverageBasic[]>
  } = {
    getPages,
    getBranchConfiguration,
    clearRaterAnswers,
    getInsuranceProposals,
    setRateFormAnswers,
    getRateFormAnswers,
    getMostRelevantProducts,

    loadConfiguration,
    loadSpecificProduct,
    hireProduct,
    sendRate,
    sendBudget,

    // Custom methods
    loadCoverages
  }

  return store
})
